'use client'

import { useState } from 'react'
import Image from 'next/image'
import { CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel'
import { CarouselBlockShell } from '@/components/ui/carousel-block-shell'
import { useCarouselCurrentIndex } from '@/components/ui/use-carousel-current-index'
import { IconComponent } from '@/components/ui/icon'
import { cn } from '@/lib/utils'
import type { Icon } from '@/payload-types'

export type BlogCarouselSize = 'large' | 'small'
export type BlogCarouselBackground = 'row-1' | 'row-2' | 'row-3' | 'row-4' | 'row-5' | 'card-bg'

const backgroundClassMap: Record<BlogCarouselBackground, string> = {
  'row-1': 'bg-theme-row-1',
  'row-2': 'bg-theme-row-2',
  'row-3': 'bg-theme-row-3',
  'row-4': 'bg-theme-row-4',
  'row-5': 'bg-theme-row-5',
  'card-bg': 'bg-theme-card-background',
}

export type BlogCarouselImage = {
  alt: string
  height?: number
  url: string
  width?: number
}

export type BlogCarouselPost = {
  id: string
  title: string
  description?: string | null
  href: string
  featuredImage?: BlogCarouselImage | null
}

export function BlogCarouselClient({
  posts,
  showFeaturedImage = true,
  size = 'large',
  background = 'card-bg',
  icon,
  iconLeft,
  iconRight,
}: {
  posts: BlogCarouselPost[]
  showFeaturedImage?: boolean | null
  size?: BlogCarouselSize | null
  background?: BlogCarouselBackground | null
  icon?: (string | null) | Icon
  iconLeft?: (string | null) | Icon
  iconRight?: (string | null) | Icon
}) {
  const [api, setApi] = useState<CarouselApi>()
  const items = Array.isArray(posts) ? posts : []
  const count = items.length
  const current = useCarouselCurrentIndex(api, count)

  if (!items.length) return null

  const isSmall = size === 'small'
  const renderedPosts = count === 3 ? [...items, ...items] : items
  const slideBasisClass = isSmall
    ? cn(
        'basis-[62%] sm:basis-[42%]',
        count <= 2
          ? 'md:basis-[34%] lg:basis-[26%]'
          : count === 3
            ? 'md:basis-[28%] lg:basis-[22%]'
            : 'md:basis-[24%] lg:basis-[18%]',
      )
    : cn(
        'basis-[85%] sm:basis-[75%]',
        count <= 2
          ? 'md:basis-[70%] lg:basis-[65%]'
          : count === 3
            ? 'basis-3/4 md:basis-1/2 lg:basis-[35%]'
            : 'md:basis-1/2 lg:basis-1/3',
      )
  const cardMaxWidthClass = isSmall
    ? 'max-w-[220px] md:max-w-[240px] lg:max-w-[260px]'
    : 'max-w-[80vw] md:max-w-125'
  const imageSizes = isSmall
    ? '(max-width: 640px) 62vw, (max-width: 768px) 42vw, (max-width: 1024px) 28vw, 260px'
    : '(max-width: 640px) 85vw, (max-width: 768px) 75vw, (max-width: 1024px) 50vw, 33vw'
  const backgroundClass = backgroundClassMap[background ?? 'card-bg']

  return (
    <CarouselBlockShell
      ariaLabel="Blog carousel"
      setApi={setApi}
      opts={{ align: 'center', containScroll: false, loop: count > 1 }}
      iconLeft={iconLeft}
      iconRight={iconRight}
      count={count}
      current={current}
      onSelect={(index) => api?.scrollTo(index)}
    >
      <CarouselContent className="ml-0">
        {renderedPosts.map((post, i) => {
          const normalizedIndex = i % count
          const isCurrent = normalizedIndex === current
          const image = showFeaturedImage ? post.featuredImage : null

          return (
            <CarouselItem key={`${post.id}-${i}`} className={cn(slideBasisClass, 'pl-0')}>
              <a href={post.href} className="no-underline">
                <article
                  className={cn(
                    'theme-card relative mx-auto mt-4 mb-6 flex w-full flex-col overflow-hidden border transition-all',
                    cardMaxWidthClass,
                    !isCurrent && 'scale-95 origin-center',
                  )}
                >
                  {icon ? (
                    <IconComponent
                      icon={icon}
                      className={cn(
                        'absolute -top-4 -right-4 z-10 h-12 w-12 transition-all duration-700 animate-twinkle',
                        isCurrent ? 'scale-100' : 'scale-0',
                      )}
                    />
                  ) : null}
                  {image ? (
                    <div className="relative aspect-[4/3] w-full">
                      <Image
                        src={image.url}
                        alt={image.alt}
                        fill
                        sizes={imageSizes}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ) : null}
                  <div
                    className={cn(
                      'flex flex-1 flex-col items-center justify-center px-4 py-5 text-center',
                      'text-theme-card-primary',
                      backgroundClass,
                      image && 'theme-card-divider border-t',
                      image ? 'min-h-[9rem]' : isSmall ? 'min-h-[14rem]' : 'min-h-[18rem]',
                    )}
                  >
                    <h3>{post.title}</h3>
                    {post.description ? (
                      <p className={cn('theme-card-content mt-2 font-normal', isSmall && 'text-sm')}>
                        {post.description}
                      </p>
                    ) : null}
                  </div>
                </article>
              </a>
            </CarouselItem>
          )
        })}
      </CarouselContent>
    </CarouselBlockShell>
  )
}
